import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import ErrorBoundaryResettable from 'components/Kizen/ErrorBoundaryResettable';
import { INITIAL_KICODE_STATE, kiCodeReducer } from './kiCodeState';
import { useKiCodeAvailability } from './useKiCodeAvailability';
import { KiCodeContext, useKiCode, type KiCodeApi } from './useKiCode';
import { isSameKiCodeTarget, type KiCodeOpenArgs } from './profiles';
import { guardKiCodeSurface } from './activeSurface';
import { KiCodePanelFrame } from './panel/KiCodePanelFrame';
import { KiCodeSession } from './session/KiCodeSession';
import {
  loadKiCodeResume,
  saveKiCodeResume,
  type KiCodeResume,
} from './session/resumeStorage';

const KICODE_ROOT_ID = 'kicode-root';

const getKiCodeRoot = () => {
  const existing = document.getElementById(KICODE_ROOT_ID);
  if (existing) return existing;
  const root = document.createElement('div');
  root.id = KICODE_ROOT_ID;
  document.body.appendChild(root);
  return root;
};

const CloseKiCodeFallback = ({ onReset }: { onReset: () => void }) => {
  const { close, session } = useKiCode();
  const [failed] = useState(session);
  useEffect(close, [close]);
  useEffect(() => {
    if (session && session !== failed) onReset();
  }, [session, failed, onReset]);
  return null;
};

export interface KiCodeProviderProps {
  children: ReactNode;
}

export const KiCodeProvider: FC<KiCodeProviderProps> = ({ children }) => {
  const [container] = useState(getKiCodeRoot);
  const [state, dispatch] = useReducer(kiCodeReducer, INITIAL_KICODE_STATE);

  const { available, identity } = useKiCodeAvailability();
  if (available !== state.available || identity !== state.identity) {
    dispatch({
      type: 'availability',
      available,
      identity,
      resume: available ? loadKiCodeResume(identity) : null,
    });
  }

  const { session } = state;
  const target = session?.target ?? null;
  const open = useCallback(
    (args: KiCodeOpenArgs) => {
      const replace = () => dispatch({ type: 'open', args });
      if (target && isSameKiCodeTarget(target, args)) replace();
      else guardKiCodeSurface(replace);
    },
    [target]
  );
  const close = useCallback(() => dispatch({ type: 'close' }), []);
  const startNewConversation = useCallback(
    () => dispatch({ type: 'new_conversation' }),
    []
  );
  const onAppCreated = useCallback(
    (appApiName: string) => dispatch({ type: 'app_created', appApiName }),
    []
  );

  const onResumable = useCallback(
    (resume: KiCodeResume) => saveKiCodeResume(identity, resume),
    [identity]
  );

  const api = useMemo<KiCodeApi>(
    () => ({ open, close, session: target }),
    [open, close, target]
  );

  const panel = useMemo(
    () =>
      session ? (
        <ErrorBoundaryResettable
          key={session.key}
          FallbackComponent={CloseKiCodeFallback}
          sentryErrorBoundary
        >
          <KiCodeSession
            session={session}
            onClose={close}
            onStartNewConversation={startNewConversation}
            onAppCreated={onAppCreated}
            onResumable={onResumable}
          />
        </ErrorBoundaryResettable>
      ) : null,
    [session, close, startNewConversation, onAppCreated, onResumable]
  );

  return (
    <KiCodeContext.Provider value={api}>
      {children}
      {createPortal(
        <ErrorBoundaryResettable
          FallbackComponent={CloseKiCodeFallback}
          sentryErrorBoundary
        >
          <KiCodePanelFrame>{panel}</KiCodePanelFrame>
        </ErrorBoundaryResettable>,
        container
      )}
    </KiCodeContext.Provider>
  );
};
